import React, { PropsWithChildren } from "react"
import { useLocation } from "@reach/router"

import defaultImage from "../../images/socials/social-image.png"
import logoImage from "../../images/logo/png/logo_512.png"
import { useSiteMetadata } from "~utils/hooks/site-metadata"

type Script = Record<string, string | boolean>

type Article = {
    publishedTime?: string
    modifiedTime?: string
    author?: string
}

type BreadcrumbItem = {
    name: string
    path: string
}

type SEOProps = {
    title?: string
    description?: string
    imageUrl?: string
    imageRelative?: string
    type?: string
    noindex?: boolean
    article?: Article
    breadcrumbs?: BreadcrumbItem[]
    scripts?: Script[]
} & PropsWithChildren

const SEO: React.FC<SEOProps> = ({
    title,
    description,
    imageUrl,
    imageRelative,
    type = "website",
    noindex,
    article,
    breadcrumbs,
    scripts,
    children,
}) => {
    const { pathname } = useLocation()
    const {
        title: defaultTitle,
        description: defaultDescription,
        siteUrl,
        twitterUsername,
    } = useSiteMetadata()

    const isDefaultImage = !imageUrl && !imageRelative
    const seo = {
        title: title || defaultTitle,
        description: description || defaultDescription,
        image: imageUrl || `${siteUrl}${imageRelative || defaultImage}`,
        url: `${siteUrl}${pathname}`,
    }

    return (
        <>
            <title>
                {seo.title}
                {title && " | Chckvet"}
            </title>
            {noindex && (
                <meta name="robots" content="noindex, nofollow" />
            )}
            <meta name="description" content={seo.description} />
            <meta name="theme-color" content="#13D879" />
            <link rel="canonical" href={seo.url} />
            {pathname === "/" && (
                <meta
                    name="facebook-domain-verification"
                    content="gn9ngeb2wwjbvculu6l2k9ejigucoi"
                />
            )}
            {seo.url && <meta property="og:url" content={seo.url} />}

            <meta property="og:type" content={type} />
            <meta property="og:site_name" content="Chckvet" />

            {seo.title && <meta property="og:title" content={seo.title} />}

            {seo.description && (
                <meta property="og:description" content={seo.description} />
            )}

            {seo.image && <meta property="og:image" content={seo.image} />}
            {seo.image && (
                <meta property="og:image:alt" content={seo.title} />
            )}
            {isDefaultImage && (
                <meta property="og:image:width" content="1200" />
            )}
            {isDefaultImage && (
                <meta property="og:image:height" content="675" />
            )}

            {article?.publishedTime && (
                <meta
                    property="article:published_time"
                    content={article.publishedTime}
                />
            )}
            {article?.modifiedTime && (
                <meta
                    property="article:modified_time"
                    content={article.modifiedTime}
                />
            )}
            {article?.author && (
                <meta property="article:author" content={article.author} />
            )}
            {article?.author && (
                <meta name="author" content={article.author} />
            )}

            <meta name="twitter:card" content="summary_large_image" />

            {twitterUsername && (
                <meta name="twitter:site" content={twitterUsername} />
            )}

            {twitterUsername && (
                <meta name="twitter:creator" content={twitterUsername} />
            )}

            {seo.title && <meta name="twitter:title" content={seo.title} />}

            {seo.description && (
                <meta name="twitter:description" content={seo.description} />
            )}

            {seo.image && <meta name="twitter:image" content={seo.image} />}
            {seo.image && (
                <meta name="twitter:image:alt" content={seo.title} />
            )}
            {pathname === "/" && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        name: "Chckvet",
                        url: siteUrl,
                        logo: `${siteUrl}${logoImage}`,
                        description: defaultDescription,
                        sameAs: [
                            "https://www.linkedin.com/company/chckvet",
                            "https://www.facebook.com/chckvet",
                            "https://www.twitter.com/chckvet",
                            "https://www.instagram.com/chckvet",
                        ],
                    })}
                </script>
            )}
            {pathname === "/" && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        name: "Chckvet",
                        url: siteUrl,
                    })}
                </script>
            )}
            {pathname === "/" && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        name: "Chckvet",
                        applicationCategory: "BusinessApplication",
                        operatingSystem: "Web",
                        url: siteUrl,
                        description: defaultDescription,
                        offers: {
                            "@type": "Offer",
                            price: "349",
                            priceCurrency: "USD",
                        },
                    })}
                </script>
            )}
            {breadcrumbs && breadcrumbs.length > 0 && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        itemListElement: breadcrumbs.map((crumb, index) => ({
                            "@type": "ListItem",
                            position: index + 1,
                            name: crumb.name,
                            item: `${siteUrl}${crumb.path}`,
                        })),
                    })}
                </script>
            )}
            {article && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BlogPosting",
                        headline: seo.title,
                        description: seo.description,
                        image: seo.image,
                        datePublished: article.publishedTime,
                        dateModified:
                            article.modifiedTime || article.publishedTime,
                        author: article.author
                            ? {
                                  "@type": "Person",
                                  name: article.author,
                              }
                            : undefined,
                        publisher: {
                            "@type": "Organization",
                            name: "Chckvet",
                            logo: {
                                "@type": "ImageObject",
                                url: `${siteUrl}${logoImage}`,
                            },
                        },
                        mainEntityOfPage: seo.url,
                    })}
                </script>
            )}
            <script>
                {`
                    !function () {
                        var reb2b = window.reb2b = window.reb2b || [];
                        if (reb2b.invoked) return;
                        reb2b.invoked = true;
                        reb2b.methods = ["identify", "collect"];
                        reb2b.factory = function (method) {
                            return function () {
                                var args = Array.prototype.slice.call(arguments);
                                args.unshift(method);
                                reb2b.push(args);
                                return reb2b;
                            };
                        };
                        for (var i = 0; i < reb2b.methods.length; i++) {
                            var key = reb2b.methods[i];
                            reb2b[key] = reb2b.factory(key);
                        }
                        reb2b.load = function (key) {
                            var script = document.createElement("script");
                            script.type = "text/javascript";
                            script.async = true;
                            script.src = "https://s3-us-west-2.amazonaws.com/b2bjsstore/b/" + key + "/reb2b.js.gz";
                            var first = document.getElementsByTagName("script")[0];
                            first.parentNode.insertBefore(script, first);
                        };
                        reb2b.SNIPPET_VERSION = "1.0.1";
                        reb2b.load("W6Z57H0L32OX");
                    }();
                `}
            </script>
            {scripts?.map(script => {
                const { ...attributes } = script
                return <script key={script.src as string} {...attributes} />
            })}
            {children}
        </>
    )
}

export default SEO
